import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vacancyService } from '../../services/vacancyService';
import { authService } from '../../services/authService';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import EmployeeNav from '../../components/EmployeeNav';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';
import { STORAGE_BASE_URL } from '../../config/api';
import { toast } from 'react-toastify';

const VacancyList = () => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [vacancies, setVacancies] = useState([]);
    const [savedVacancies, setSavedVacancies] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    type_of_work: '',
    presence_type: '',
    salary_min: '',
    salary_max: '',
  });
  const [tempFilters, setTempFilters] = useState({
    status: '',
    type_of_work: '',
    presence_type: '',
    salary_min: '',
    salary_max: '',
  });
  const [salaryRange, setSalaryRange] = useState({ min: 0, max: 100000 });
  const [sliderValues, setSliderValues] = useState({ min: 0, max: 100000 });

    useEffect(() => {
        loadVacancies();
        loadSavedVacancies();
    }, [filters]);


    const loadVacancies = async () => {
        try {
            setLoading(true);
            // Clean filters - remove empty strings
            const cleanedFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) => value !== '')
            );
            const response = await vacancyService.getAll(cleanedFilters);
            let jobs = [];
            if (response && response.success && response.data) {
                jobs = Array.isArray(response.data) ? response.data : [];
            } else if (Array.isArray(response)) {
                jobs = response;
            }
            setVacancies(jobs);
        } catch (error) {
            console.error('Error fetching vacancies:', error);
            toast.error(t('employee.vacancyList.loadFailed') || 'Failed to load vacancies');
            setVacancies([]);
        } finally {
            setLoading(false);
        }
    };

    const loadSavedVacancies = async () => {
        try {
            const response = await vacancyService.getSaved();
            if (response.success) {
                const savedIds = new Set(response.data.map(v => v.id));
                setSavedVacancies(savedIds);
            }
        } catch (error) {
            console.error('Failed to load saved vacancies');
        }
    };

  const handleFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    // Apply temporary filters to actual filters, including slider values
    const salaryMin = sliderValues.min > salaryRange.min ? sliderValues.min.toString() : '';
    const salaryMax = sliderValues.max < salaryRange.max ? sliderValues.max.toString() : '';

    setFilters(prev => ({
      ...prev,
      status: tempFilters.status,
      type_of_work: tempFilters.type_of_work,
      presence_type: tempFilters.presence_type,
      salary_min: salaryMin,
      salary_max: salaryMax,
    }));
    setFiltersExpanded(false);
  };

  // Initialize temp filters when filter panel opens
  useEffect(() => {
    if (filtersExpanded) {
      setTempFilters({
        status: filters.status,
        type_of_work: filters.type_of_work,
        presence_type: filters.presence_type,
        salary_min: filters.salary_min,
        salary_max: filters.salary_max,
      });
      // Initialize slider values from current filters
      if (filters.salary_min) {
        const minVal = parseInt(filters.salary_min);
        if (minVal && minVal >= salaryRange.min) {
          setSliderValues(prev => ({ ...prev, min: minVal }));
        } else {
          setSliderValues(prev => ({ ...prev, min: salaryRange.min }));
        }
      } else {
        setSliderValues(prev => ({ ...prev, min: salaryRange.min }));
      }
      if (filters.salary_max) {
        const maxVal = parseInt(filters.salary_max);
        if (maxVal && maxVal <= salaryRange.max) {
          setSliderValues(prev => ({ ...prev, max: maxVal }));
        } else {
          setSliderValues(prev => ({ ...prev, max: salaryRange.max }));
        }
      } else {
        setSliderValues(prev => ({ ...prev, max: salaryRange.max }));
      }
    }
  }, [filtersExpanded]);

    const clearFilters = () => {
        setTempFilters({
            status: '',
            type_of_work: '',
            presence_type: '',
            salary_min: '',
            salary_max: '',
        });
        setSliderValues({ min: salaryRange.min, max: salaryRange.max });
    };

    const formatSalarySlider = (value) => {
        if (value >= 1000) {
            return `$${Math.round(value / 1000)}K`;
        }
        return `$${value}`;
    };

    const handleSliderChange = (type, value) => {
        const numValue = parseInt(value) || 0;
        if (type === 'min') {
            const newMin = Math.min(numValue, sliderValues.max - 1000);
            setSliderValues(prev => ({ ...prev, min: newMin }));
            setTempFilters(prev => ({ ...prev, salary_min: newMin > salaryRange.min ? newMin.toString() : '' }));
        } else {
            const newMax = Math.max(numValue, sliderValues.min + 1000);
            setSliderValues(prev => ({ ...prev, max: newMax }));
            setTempFilters(prev => ({ ...prev, salary_max: newMax < salaryRange.max ? newMax.toString() : '' }));
        }
    };

    const getSliderPercentage = (value) => {
        return ((value - salaryRange.min) / (salaryRange.max - salaryRange.min)) * 100;
    };

    const hasActiveFilters = () => {
        const hasSalaryFilter = tempFilters.salary_min || tempFilters.salary_max ||
            sliderValues.min !== salaryRange.min || sliderValues.max !== salaryRange.max;
        return tempFilters.status || tempFilters.type_of_work || tempFilters.presence_type || hasSalaryFilter;
    };

    const handleJobClick = (jobId) => {
        navigate(`/employee/vacancies/${jobId}`);
    };

    const handleSave = async (jobId, e) => {
        e.stopPropagation();
        try {
            await vacancyService.save(jobId);
            setSavedVacancies(prev => new Set([...prev, jobId]));
            toast.success(t('employee.vacancyList.saveSuccess') || 'Vacancy saved successfully');
        } catch (error) {
            toast.error(t('employee.vacancyList.saveFailed') || 'Failed to save vacancy');
        }
    };

    const handleUnsave = async (jobId, e) => {
        e.stopPropagation();
        try {
            await vacancyService.unsave(jobId);
            setSavedVacancies(prev => {
                const newSet = new Set(prev);
                newSet.delete(jobId);
                return newSet;
            });
            toast.success(t('employee.vacancyList.unsaveSuccess') || 'Vacancy unsaved');
        } catch (error) {
            toast.error(t('employee.vacancyList.unsaveFailed') || 'Failed to unsave vacancy');
        }
    };

    const formatSalary = (salary, currency) => {
        const currencyTranslation = {
            'USD': t('employee.home.currency.USD'),
            'EUR': t('employee.home.currency.EUR'),
            'EGP': t('employee.home.currency.EGP'),
        };

        if (!salary) return t('employee.home.salaryNotSpecified');
        // show 18000 as 18k , 7500 as 7.5k
        if (salary >= 1000) {
            if (salary % 1000 === 0) {
                return `${Math.round(salary / 1000)}k ${currencyTranslation[currency] || currency} ${t('employee.home.perMonth')}`;
            } else {
                return `${(salary / 1000).toFixed(1)}k ${currencyTranslation[currency] || currency} ${t('employee.home.perMonth')}`;
            }
        }
    };

    const formatWorkType = (type) => {
        const types = {
            'full_time': t('employee.home.workType.fullTime'),
            'part_time': t('employee.home.workType.partTime')
        };
        return types[type] || type;
    };

    const formatPresenceType = (type) => {
        const types = {
            'remote': t('employee.home.presenceType.remote'),
            'hybrid': t('employee.home.presenceType.hybrid'),
            'onsite': t('employee.home.presenceType.onsite')
        };
        return types[type] || type;
    };

    const formatLocation = (location) => {
        if (!location) return t('employee.vacancyDetail.defaultLocation');
        if (typeof location === 'string') return location;
        if (typeof location === 'object' && (location.lat || location.long)) {
            return t('employee.vacancyDetail.defaultLocation');
        }
        return t('employee.vacancyDetail.defaultLocation');
  };

  return (
        <div className="vacancy-list-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <EmployeeNav user={user} />

            {/* Header */}
            <div className="px-3 mb-3 mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="mb-0">{t('employee.vacancyList.title') || 'Job Vacancies'}</h4>
                    <button
                        className="btn-back"
                        onClick={() => navigate('/employee/home')}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '8px',
                            cursor: 'pointer'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Search Bar with Filter Icon */}
            <div className="search-section px-3 mb-3">
                <div className="search-bar-container position-relative" style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f5f5f5',
                    borderRadius: '12px',
                    padding: '0',
                    border: 'none'
                }}>
                    {/* Filter Icon Button (Left) */}
                    <button
                        type="button"
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '12px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#111'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="4" y1="6" x2="20" y2="6"></line>
                            <line x1="4" y1="12" x2="20" y2="12"></line>
                            <line x1="4" y1="18" x2="20" y2="18"></line>
                            <circle cx="8" cy="6" r="2"></circle>
                            <circle cx="16" cy="12" r="2"></circle>
                            <circle cx="8" cy="18" r="2"></circle>
                        </svg>
                    </button>

                    {/* Search Input */}
                    <input
                        type="text"
                        className="search-input form-control"
                        placeholder={t('employee.vacancyList.searchPlaceholder') || 'البحث باسم الوظيفة'}
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        style={{
                            flex: 1,
                            border: 'none',
                            background: 'transparent',
                            padding: '12px 0',
                            fontSize: '14px',
                            outline: 'none',
                            color: '#111'
                        }}
                    />

                    {/* Search Icon (Right) */}
                    <button
                        type="button"
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '12px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#111'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.35-4.35"></path>
                        </svg>
                    </button>
                </div>
      </div>

            {/* Filter Modal/Overlay */}
            {filtersExpanded && (
                <>
                    {/* Overlay */}
                    <div
                        onClick={() => setFiltersExpanded(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1000,
                            animation: 'fadeIn 0.3s ease'
                        }}
                    />

                    {/* Filter Panel */}
                    <div
                        className="filters-panel"
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'white',
                            borderTopLeftRadius: '24px',
                            borderTopRightRadius: '24px',
                            padding: '24px',
                            zIndex: 1001,
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
                            animation: 'slideUp 0.3s ease'
                        }}
                    >
                        {/* Filter Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '24px',
                            paddingBottom: '16px',
                            borderBottom: '1px solid #e5e7eb'
                        }}>
                            <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111' }}>
                                {t('employee.vacancyList.filters') || 'Filters'}
                            </h4>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {hasActiveFilters() && (
                                    <button
                                        onClick={clearFilters}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc2626',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            padding: '4px 8px'
                                        }}
                                    >
                                        {t('employee.vacancyList.clear') || 'Clear'}
                                    </button>
                                )}
                                <button
                                    onClick={() => setFiltersExpanded(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: '4px',
                                        cursor: 'pointer',
                                        color: '#666',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
        </div>

                        {/* Filter Content */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {/* Work Type Filter */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '10px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#4a7bc4'
                                }}>
                                    {t('employee.vacancyList.workType') || 'Work Type'}
                                </label>
          <select
            value={tempFilters.type_of_work}
            onChange={(e) => handleFilterChange('type_of_work', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1.5px solid #e5e7eb',
                                        borderRadius: '12px',
                                        fontSize: '14px',
                                        background: '#f9fafb',
                                        color: '#111',
                                        cursor: 'pointer',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#608fd3';
                                        e.target.style.background = '#fff';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.background = '#f9fafb';
                                    }}
                                >
                                    <option value="">{t('employee.vacancyList.allWorkTypes') || 'All Work Types'}</option>
                                    <option value="full_time">{t('employee.home.workType.fullTime')}</option>
                                    <option value="part_time">{t('employee.home.workType.partTime')}</option>
          </select>
        </div>

                            {/* Presence Type Filter */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '10px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#4a7bc4'
                                }}>
                                    {t('employee.vacancyList.presenceType') || 'Presence Type'}
                                </label>
          <select
            value={tempFilters.presence_type}
            onChange={(e) => handleFilterChange('presence_type', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1.5px solid #e5e7eb',
                                        borderRadius: '12px',
                                        fontSize: '14px',
                                        background: '#f9fafb',
                                        color: '#111',
                                        cursor: 'pointer',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#608fd3';
                                        e.target.style.background = '#fff';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.background = '#f9fafb';
                                    }}
                                >
                                    <option value="">{t('employee.vacancyList.allPresenceTypes') || 'All Presence Types'}</option>
                                    <option value="remote">{t('employee.home.presenceType.remote')}</option>
                                    <option value="hybrid">{t('employee.home.presenceType.hybrid')}</option>
                                    <option value="onsite">{t('employee.home.presenceType.onsite')}</option>
          </select>
        </div>

                            {/* Status Filter */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '10px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#4a7bc4'
                                }}>
                                    {t('employee.vacancyList.status') || 'Status'}
                                </label>
                                <select
                                    value={tempFilters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: '1.5px solid #e5e7eb',
                                        borderRadius: '12px',
                                        fontSize: '14px',
                                        background: '#f9fafb',
                                        color: '#111',
                                        cursor: 'pointer',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#608fd3';
                                        e.target.style.background = '#fff';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.background = '#f9fafb';
                                    }}
                                >
                                    <option value="">{t('employee.vacancyList.allStatuses') || 'All Statuses'}</option>
                                    <option value="open">{t('employee.vacancyList.open') || 'Open'}</option>
                                    <option value="closed">{t('employee.vacancyList.closed') || 'Closed'}</option>
                                </select>
                            </div>

                            {/* Salary Range Filter */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '20px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#4a7bc4'
                                }}>
                                    {t('employee.vacancyList.salaryRange') || 'Salary Range'}
                                </label>

                                {/* Range Slider */}
                                <div style={{ position: 'relative', padding: '20px 0 50px 0' }}>
                                    {/* End Labels */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '12px',
                                        fontSize: '12px',
                                        color: '#6b7280',
                                        fontWeight: '500'
                                    }}>
                                        <span>{t('employee.vacancyList.less') || 'Less'}</span>
                                        <span>{t('employee.vacancyList.more') || 'More'}</span>
                                    </div>

                                    {/* Slider Track Container */}
                                    <div style={{
                                        position: 'relative',
                                        height: '6px',
                                        background: '#e5e7eb',
                                        borderRadius: '3px',
                                        marginBottom: '30px'
                                    }}>
                                        {/* Selected Range */}
                                        <div style={{
                                            position: 'absolute',
                                            left: `${getSliderPercentage(sliderValues.min)}%`,
                                            width: `${getSliderPercentage(sliderValues.max) - getSliderPercentage(sliderValues.min)}%`,
                                            height: '6px',
                                            background: '#4a7bc4',
                                            borderRadius: '3px',
                                            zIndex: 1
                                        }} />

                                        {/* Min Handle */}
                                        <input
                                            type="range"
                                            min={salaryRange.min}
                                            max={sliderValues.max - 1000}
                                            value={sliderValues.min}
                                            onChange={(e) => handleSliderChange('min', e.target.value)}
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                width: '100%',
                                                height: '6px',
                                                margin: 0,
                                                opacity: 0,
                                                zIndex: 3,
                                                cursor: 'pointer',
                                                pointerEvents: 'auto'
                                            }}
                                        />
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: `calc(${getSliderPercentage(sliderValues.min)}% - 12px)`,
                                                top: '-9px',
                                                width: '24px',
                                                height: '24px',
                                                background: '#4a7bc4',
                                                borderRadius: '50%',
                                                border: '2px solid white',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                zIndex: 2,
                                                pointerEvents: 'none'
                                            }}
                                        />

                                        {/* Max Handle */}
          <input
                                            type="range"
                                            min={sliderValues.min + 1000}
                                            max={salaryRange.max}
                                            value={sliderValues.max}
                                            onChange={(e) => handleSliderChange('max', e.target.value)}
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                width: '100%',
                                                height: '6px',
                                                margin: 0,
                                                opacity: 0,
                                                zIndex: 3,
                                                cursor: 'pointer',
                                                pointerEvents: 'auto'
                                            }}
                                        />
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: `calc(${getSliderPercentage(sliderValues.max)}% - 12px)`,
                                                top: '-9px',
                                                width: '24px',
                                                height: '24px',
                                                background: '#4a7bc4',
                                                borderRadius: '50%',
                                                border: '2px solid white',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                zIndex: 2,
                                                pointerEvents: 'none'
                                            }}
                                        />
                                    </div>

                                    {/* Value Labels */}
                                    <div style={{
                                        position: 'relative',
                                        height: '20px'
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            left: `max(0px, calc(${getSliderPercentage(sliderValues.min)}% - 25px))`,
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#4a7bc4',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {formatSalarySlider(sliderValues.min)}
                                        </div>
                                        <div style={{
                                            position: 'absolute',
                                            right: `max(0px, calc(${100 - getSliderPercentage(sliderValues.max)}% - 25px))`,
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#4a7bc4',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {formatSalarySlider(sliderValues.max)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Apply Button */}
                        <button
                            onClick={handleApplyFilters}
                            style={{
                                width: '100%',
                                marginTop: '24px',
                                padding: '14px',
                                background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(96, 143, 211, 0.3)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 6px 16px rgba(96, 143, 211, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 12px rgba(96, 143, 211, 0.3)';
                            }}
                        >
                            {t('employee.vacancyList.apply') || 'Apply Filters'}
                        </button>
                    </div>
                </>
            )}

            {/* Vacancies List */}
            <div className="all-jobs-section px-3 mb-5">
                <div className="jobs-list">
                    {loading ? (
                        <Loader size="medium" />
                    ) : vacancies.length > 0 ? (
                        vacancies.map((job) => {
                            const targetCompany = job.target_company || job.company_details?.target_company || job.company;
                            const companyName = job.target_company_name || targetCompany?.name || t('employee.home.companyName');
                            const companyLogo = targetCompany?.logo || targetCompany?.image;
                            const isSaved = savedVacancies.has(job.id);

                            return (
                                <div key={job.id} className="job-card mb-3">
                                    <div className="d-flex align-items-start gap-2">
                                        <div className="company-logo-card">
                                            {companyLogo ? (
                                                <img
                                                    src={companyLogo.startsWith('http') ? companyLogo : `${STORAGE_BASE_URL}/${companyLogo}`}
                                                    alt="Company Logo"
                                                    className="logo-img"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div className="company-logo-text text-dark align-content-center" style={{ display: companyLogo ? 'none' : 'flex' }}>
                                                <b>{companyName?.charAt(0).toUpperCase() || 'C'}</b>
                                            </div>
                                        </div>
                                        <div className={`job-content flex-grow-1 ${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                                            <div className="job-title-card mb-2">
                                                {job.job_title?.name || t('employee.home.jobTitle')}
                                            </div>
                                            <div className="job-company-location mb-2">
                                                {companyName} - {formatLocation(job.location)} - {t('employee.vacancyDetail.egypt') || 'Egypt'}
                                            </div>
                                            <div className="job-tags d-flex gap-2 mb-2 mt-3">
                                                <span className="job-tag">{formatPresenceType(job.presence_type)}</span>
                                                <span className="job-tag">{formatWorkType(job.type_of_work)}</span>
                                            </div>
                                        </div>
                                        <div className={`d-flex flex-column justify-content-between book-mark-div ${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                                            <button
                                                className="bookmark-btn"
                                                onClick={(e) => isSaved ? handleUnsave(job.id, e) : handleSave(job.id, e)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: '4px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {isSaved ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                                                    </svg>
                                                )}
                                            </button>
                                            <div className='salary-tag-container'>
                                                <span className='salary-tag mb-3 d-block'>
                                                    {formatSalary(job.salary, job.currency)}
                                                </span>
                                                {job.is_open && (<>
                                                    {job.is_applied ? (
                                                        <button className="btn-apply btn-applied"
                                                        onClick={() => handleJobClick(job.id)}

                                                        >
                                                            {t('employee.home.applied')}

                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn-apply"
                                                            onClick={() => handleJobClick(job.id)}
                                                        >
                                                            {t('employee.home.apply')}
                                                        </button>
                                                    )}
                                                    </>
                                                    )}
                                                    {/* check if the vacancy is closed */}
                                                    {job.is_closed && (
                                                    <button className="btn-apply btn-closed disabled">
                                                        {t('employee.home.closed')}
                                                    </button>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-4" style={{ color: '#6c757d' }}>
                            {filters.search || filters.type_of_work || filters.presence_type || filters.status
                                ? t('employee.vacancyList.noResults') || 'No vacancies found matching your filters'
                                : t('employee.home.noJobs')}
                        </div>
                    )}
        </div>
      </div>

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
    </div>
  );
};

export default VacancyList;


